// "Recently watched" — the Home rail of the clips you last played, newest
// first. The personal counterpart to Popular and New: where you left off,
// rather than what everyone else is doing.
//
// Hidden until you've played something. Watched clips are kept per device
// (AppShell's watchedUrls, localStorage) in the order they were played.
import { useMemo } from 'react';
import { History } from 'lucide-react';
import { useTechniques } from '../hooks/useTechniques';
import TechniqueTile from './TechniqueTile';
import Rail from './Rail';
import { TIER_ACCENT_COLORS } from '../constants/layout';
import { groupTechniques } from '../utils/groupTechniques';
import { TechniqueStatus, type BeltRank, type Technique } from '../types';
import { TechniqueGrid, type LibraryBridge } from './Spotlights';
import { DetailPage } from './Forum';
import { FeedSkeleton, TileRailSkeleton } from './RailSkeleton';

/** How many tiles the rail carries, matching Popular and New. */
const SHOWN = 10;

/** How many the full-screen grid goes back through. */
const ALL = 60;

export default function RecentlyWatched({ library, feed, open, onOpen, onClose }: {
  library?: LibraryBridge;
  /** Render this many clips into Home's four-across feed instead of the rail. */
  feed?: number;
  /** Render the full-screen grid of the last ALL clips instead of the rail. */
  open?: boolean;
  /** The rail's "See all" — Home flips `open` (it hides the other rails). */
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const { techniques, loading } = useTechniques();
  const recent = library?.recentlyWatched;

  // Most recent first. A clip that has since left the library is skipped;
  // a gi/no-gi pair counts as one entry, the way Library shows them.
  const watched = useMemo(() => {
    if (!recent) return [];
    const byUrl = new Map<string, Technique[]>();
    for (const t of techniques) {
      if (!t.instagramUrl) continue;
      byUrl.set(t.instagramUrl, [...(byUrl.get(t.instagramUrl) ?? []), t]);
    }
    return recent.flatMap((url) => byUrl.get(url) ?? []);
  }, [techniques, recent]);
  const rows = useMemo(() => groupTechniques(watched).slice(0, SHOWN), [watched]);

  // Home's feed: what you last played, flowing down the page. Empty until you
  // have played something, and it says so rather than showing a blank tab.
  if (feed !== undefined) {
    if (loading && recent && recent.length > 0) return <FeedSkeleton />;
    if (watched.length === 0) {
      return <div className="home-feed-empty">Nothing watched yet — play a clip and it lands here.</div>;
    }
    return <TechniqueGrid list={watched.slice(0, feed)} library={library} className="home-grid" />;
  }

  if (open) {
    return (
      <DetailPage title="History" icon={<History size={17} />} onBack={() => onClose?.()}>
        <div className="sp-scroll">
          <TechniqueGrid list={watched.slice(0, ALL)} library={library} />
        </div>
      </DetailPage>
    );
  }

  // The played list is local and there from the first frame, so we know
  // whether this rail is going to show at all: stand it in only when there is
  // something watched and the catalog it has to be matched against is still
  // loading.
  if (loading && recent && recent.length > 0) {
    return <TileRailSkeleton title="History" icon={<History size={16} />} />;
  }
  if (rows.length === 0) return null;

  return (
    <Rail title="History" icon={<History size={16} />} onSeeAll={onOpen} tiles>
      {rows.map((g) => { const t = g.lead; return (
        <div className="rail-cell" key={g.key}>
          <TechniqueTile
            id={t.id}
            name={t.name}
            status={library?.statusOf(t.id) ?? TechniqueStatus.Unchecked}
            categoryColor={TIER_ACCENT_COLORS[t.tier as BeltRank]}
            instagramUrl={t.instagramUrl}
            thumbnail={t.thumbnail}
            // Everything here is watched by definition; fading the whole row
            // would just make it look disabled.
            watched={false}
            onPipClick={library ? () => library.onPlay(t) : undefined}
            onToggleMat={library ? () => library.onToggleMat(t) : undefined}
            onInfoClick={library ? () => library.onInfo(t) : undefined}
            variantCount={g.variants.length}
            onVariantsClick={library ? () => library.onVersions(g) : undefined}
            onOpenAllVersions={library ? () => library.onOpenAllVersions(g) : undefined}
          />
        </div>
      ); })}
    </Rail>
  );
}
